/**
 * Snusify product page.
 *
 * Everything here is an enhancement of a page that already works: the buy
 * form posts to /cart/add, pack sizes are plain radios, variant choices are
 * links to each variant's URL (or a select), and the media thumbnails are
 * anchors into a scroll-snap track.
 *
 * Adding to cart belongs to snusify-cart-form, the component every product
 * form in the theme shares (assets/snusify-cart-form.js). This script adds
 * what only the product page has:
 *
 * - choosing a variant refreshes only the parts that depend on it, from the
 *   Section Rendering API, and updates the address instead of loading the
 *   page
 * - after an add to cart, the packs, stock line and button are refreshed
 *   from the section the cart form asked for, keeping the visitor's pack
 * - a buy bar follows the page once the real add button has scrolled away
 * - the thumbnails follow the track as it is swiped
 *
 * Elements that depend on the selected variant carry data-pdp-swap with a key
 * that is stable across renders, so a fresh render can be matched part for
 * part without touching app blocks in between.
 */

const ADDED_RESET_MS = 2400;

class SnusifyProduct extends HTMLElement {
  /** Recreated on every connect: a controller aborted by a disconnect
      cannot be reused, and listeners bound to it would silently never fire. */
  #abort = null;
  /** The variant fetch in flight, so a newer choice can cancel it. */
  #request = null;
  #barObserver = null;
  #mediaObserver = null;
  #resetTimer = 0;

  connectedCallback() {
    this.#abort?.abort();
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.addEventListener('change', (event) => this.#onChange(event), { signal });
    this.addEventListener('click', (event) => this.#onClick(event), { signal });
    this.addEventListener('cart:item-added', (event) => this.#onAdded(/** @type {CustomEvent} */ (event)), { signal });

    this.#syncBar();
    this.#initBuybar();
    this.#initMedia();
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#request?.abort();
    this.#barObserver?.disconnect();
    this.#mediaObserver?.disconnect();
    clearTimeout(this.#resetTimer);
  }

  /** @param {Event} event */
  #onChange(event) {
    const target = /** @type {HTMLInputElement} */ (event.target);
    if (target.matches('[data-pack-input]')) this.#syncBar();
    if (target.matches('[data-variant-input]')) this.#selectVariant(target.value);
  }

  /** @param {MouseEvent} event */
  #onClick(event) {
    const target = /** @type {Element} */ (event.target);

    const variantLink = /** @type {HTMLAnchorElement | null} */ (target.closest('[data-variant-link]'));
    if (variantLink) {
      // Let modified clicks open the variant in a new tab as links do.
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
      event.preventDefault();
      if (variantLink.getAttribute('aria-current') === 'true') return;
      this.#selectVariant(variantLink.dataset.variantLink ?? '');
      return;
    }

    if (target.closest('[data-buybar-add]')) {
      const form = this.#form();
      const add = form?.querySelector('[data-buy-add]');
      if (form && add && !add.disabled) form.requestSubmit(add);
      return;
    }

    const thumb = target.closest('[data-media-thumb]');
    if (thumb) {
      event.preventDefault();
      this.#showSlide(thumb.getAttribute('href')?.slice(1) ?? '');
    }
  }

  /** @returns {HTMLFormElement | null} */
  #form() {
    return this.querySelector('form[data-snusify-buy]');
  }

  /** Mirror the chosen pack onto the buy bar. */
  #syncBar() {
    const pack = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-pack-input]:checked'));
    if (!pack) return;
    for (const el of this.querySelectorAll('[data-buybar-total]')) el.textContent = pack.dataset.total ?? '';
    for (const el of this.querySelectorAll('[data-buybar-pack]')) el.textContent = pack.dataset.label ?? '';
  }

  /* ---------- Variants ---------- */

  /** @param {string} variantId */
  async #selectVariant(variantId) {
    const url = new URL(this.dataset.url ?? window.location.pathname, window.location.origin);
    url.searchParams.set('variant', variantId);

    const doc = await this.#fetchSection(url);
    if (!doc) return;

    this.#swap(doc);
    const address = new URL(window.location.href);
    address.searchParams.set('variant', variantId);
    history.replaceState(history.state, '', address);
  }

  /**
   * @param {URL} url - the product URL to render the section for
   * @returns {Promise<Document | null>}
   */
  async #fetchSection(url) {
    this.#request?.abort();
    this.#request = new AbortController();
    url.searchParams.set('section_id', this.dataset.sectionId ?? '');
    this.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(url, { signal: this.#request.signal });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return new DOMParser().parseFromString(await response.text(), 'text/html');
    } catch (error) {
      if (error.name === 'AbortError') return null;
      // The page can always render the variant itself.
      url.searchParams.delete('section_id');
      window.location.assign(url);
      return null;
    } finally {
      this.removeAttribute('aria-busy');
    }
  }

  /**
   * Replace each [data-pdp-swap] part with its counterpart in a fresh render.
   * @param {Document} doc
   * @param {string[]} [keys] - only these parts; all of them when omitted
   */
  #swap(doc, keys) {
    const focusedId = document.activeElement?.id;

    for (const part of this.querySelectorAll('[data-pdp-swap]')) {
      // A part inside one that was already replaced is gone with it.
      if (!part.isConnected) continue;
      const key = part.getAttribute('data-pdp-swap') ?? '';
      if (keys && !keys.includes(key)) continue;
      const fresh = doc.querySelector(`[data-pdp-swap="${CSS.escape(key)}"]`);
      if (fresh) part.replaceWith(fresh);
    }

    if (focusedId && !document.activeElement?.id) {
      document.getElementById(focusedId)?.focus({ preventScroll: true });
    }

    this.#syncBar();
    this.#initBuybar();
    this.#initMedia();
  }

  /* ---------- After an add to cart ---------- */

  /**
   * The cart form asked for this section alongside the cart link. Refresh
   * only the packs, stock line and button from it - the status line and cart
   * link beside them stay put, so the announcement is not cut off.
   * @param {CustomEvent<{ sections: Record<string, string> }>} event
   */
  #onAdded(event) {
    this.#markAdded();

    const html = event.detail?.sections?.[this.dataset.sectionId ?? ''];
    if (!html) return;

    const chosen = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-pack-input]:checked'))?.dataset.qty;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const keys = [...this.querySelectorAll('[data-pdp-swap^="packs-"]')].map((el) => el.getAttribute('data-pdp-swap') ?? '');
    this.#swap(doc, keys);
    // The fresh packs carry list prices; let the cart form price them again.
    /** @type {HTMLElement & { refresh?: () => void } | null} */ (this.querySelector('snusify-cart-form'))?.refresh?.();
    this.#syncBar();
    this.#keepPack(chosen);
  }

  /**
   * A fresh render starts on the first pack that fits. Keep the visitor's own
   * choice instead, as long as there is still stock for it.
   * @param {string | undefined} qty
   */
  #keepPack(qty) {
    if (!qty) return;
    const pack = /** @type {HTMLInputElement | null} */ (
      this.querySelector(`[data-pack-input][data-qty="${CSS.escape(qty)}"]:not(:disabled)`)
    );
    if (!pack || pack.checked) return;
    pack.checked = true;
    // Let the cart form update its button total as it would for a click.
    pack.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /** Brief confirmation on the buy bar, which may be the button that was used. */
  #markAdded() {
    const bar = this.querySelector('[data-buybar-add]');
    if (!bar) return;
    bar.classList.add('is-added');
    clearTimeout(this.#resetTimer);
    this.#resetTimer = window.setTimeout(() => bar.classList.remove('is-added'), ADDED_RESET_MS);
  }

  /* ---------- Buy bar ---------- */

  #initBuybar() {
    this.#barObserver?.disconnect();
    const bar = /** @type {HTMLElement | null} */ (this.querySelector('[data-buybar]'));
    const add = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-buy-add]'));
    if (!bar) return;

    if (!add || add.disabled) {
      bar.hidden = true;
      bar.classList.remove('is-visible');
      return;
    }

    bar.hidden = false;
    // Only once the button has gone off the top: before the visitor reaches
    // it, the page itself is still leading them to it. The root reaches far
    // below the viewport, so "intersecting" means "not yet scrolled past" and
    // the state flips as the button crosses the top edge - even when a jump
    // (a fling, the End key, a restored scroll position) carries it from
    // below the viewport to above it without ever being on screen.
    this.#barObserver = new IntersectionObserver(
      ([entry]) => bar.classList.toggle('is-visible', !entry.isIntersecting),
      { rootMargin: '0px 0px 100000px 0px' }
    );
    this.#barObserver.observe(add);
  }

  /* ---------- Media ---------- */

  #initMedia() {
    this.#mediaObserver?.disconnect();
    const track = this.querySelector('[data-media-track]');
    const thumbs = [...this.querySelectorAll('[data-media-thumb]')];
    if (!track || thumbs.length < 2) return;

    this.#mediaObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          this.#markThumb(entry.target.id);
        }
      },
      { root: track, threshold: 0.6 }
    );
    for (const slide of track.querySelectorAll('[data-media-slide]')) this.#mediaObserver.observe(slide);
  }

  /** @param {string} id - the slide's element id */
  #showSlide(id) {
    const slide = id ? document.getElementById(id) : null;
    const track = this.querySelector('[data-media-track]');
    if (!slide || !track) return;
    track.scrollTo({ left: slide.offsetLeft - track.offsetLeft });
    this.#markThumb(id);
  }

  /** @param {string} id */
  #markThumb(id) {
    for (const thumb of this.querySelectorAll('[data-media-thumb]')) {
      if (thumb.getAttribute('href') === `#${id}`) thumb.setAttribute('aria-current', 'true');
      else thumb.removeAttribute('aria-current');
    }
  }
}

if (!customElements.get('snusify-product')) {
  customElements.define('snusify-product', SnusifyProduct);
}
